import { Moment } from 'moment';
import { BehaviorSubject } from 'rxjs';

import * as materialCodes from '@shared-constants/material-codes-constants';
import { BasicInterfaceTypeEnum, CatsNoBillCodeDescriptionEnum } from '@shared-enums/cats.enum';
import { arrayIsTruthy, syncObjectProperties } from '@shared-helpers/array-object-helpers';
import { convertSapDateToHumanReadable, convertSapDateToMoment, getOdataString } from '@shared-helpers/date-helpers';
import { getFirstChar, safeCompare, trimLeadingZeros } from '@shared-helpers/string-number-helpers';
import {
  IConditionToDefault,
  IConditionToMaterialResult,
  ICondTimesheetHeaderResult,
  IHttpResponseContract,
  ISapResponseContractCondition,
  ISkfConfigEntity,
} from '@shared-interfaces/api';

import { ISelectOptions, ITableConfigEntity } from '../../interfaces/internal-interfaces';
import { DatahubService } from '../../services/datahub.service';
import { ConditionHandler } from '../ConditionHandler';
import { Equipment } from '../equipment';
import { ExtDocument } from '../extDocument';
import { ProductionGroup } from '../Production-Group/ProductionGroup';
import { ProductionGroupQuantity } from '../Production-Group/ProductionGroupQuantity';
import { ProductionGroupText } from '../Production-Group/ProductionGroupText';
import { makeComplexConfigItem, makeSelectOption } from '../table-configs/table-config-helpers';

export class ContractHandler {
  private readonly skfsReadySubject$ = new BehaviorSubject(false);
  //tracked fields
  __areConditionsFetched = false;
  __textSKF: ISkfConfigEntity[] = [];
  __quantitySKF: ISkfConfigEntity[] = [];
  __equipmentSKF: ISkfConfigEntity[] = [];
  public readonly __skfsReady$ = this.skfsReadySubject$.asObservable();

  private readonly supportedSKFTypes: ReadonlyArray<ISupportedSkfTypes> = [
    'PRODUCTION',
    'TEXT',
    'EQUIPMENT',
    'SELECT',
    'BOOLEAN',
    'SHORT_TEXT',
    'TIME',
  ];

  public display = '';
  public displaySecondary = '';
  public contractName = '';
  public contractDateRange = '';
  public expirationDateFriendly = '';

  value = {
    //values found from the contract webservice
    ContractId: '',
    Item: '',
    Type: '',
    ServiceOrder: '',
    CustomerId: '',
    CustomerName: '',
    CostCenter: '',
    CostCenterDisplay: '',
    ProfitCenter: '',
    ItemText: '',
    PriceList: '',
    PriceGroup: '',
    PONumber: '',
    CondGroup: '',
    CondGroupDescription: '',
    CustGroup: '',
    CustGroupDescription: '',
    ValidFrom: '',
    ValidTo: '',
    shouldHideCloseButton: false,
    isNonbill: false,
  };

  readonly conditions: IConditionsForContract = {
    //values found on customer/condition group webservice
    // activityBillingMap:  [], //map for activities => materials
    employeeBillingMap: [], //map for employee
    equipmentBillingMap: [], //map for equipment => materials
    equipmentTypeBillingMap: [], //map for equipment type => materials
    timesheetHeaders: [],
    prdGroup: [],
    skfs: [], //
    materialList: [], //List of acceptable materials by contract
    //91 is for equipment
    //92 is for labor
  };

  // PriceList Values
  // 01 Time and Material
  // 02 Lump Sum
  // 03 Fixed Price
  // 04 Storm

  private setDisplayTextProps(): void {
    this.contractName = this.value.ItemText;
    this.expirationDateFriendly = convertSapDateToHumanReadable(this.value.ValidTo);
    this.contractDateRange = `${convertSapDateToHumanReadable(this.value.ValidFrom)} - ${this.expirationDateFriendly}`;
    this.display = `${this.contractName} \n${this.contractDateRange}`;
  }

  setValue(contract: IHttpResponseContract) {
    syncObjectProperties(contract, this.value);
    this.__areConditionsFetched = false;
    this.setDisplayTextProps();
    return this;
  }

  getConditionGrp() {
    return this.value.CondGroup;
  }

  getCustomerGroup() {
    return this.value.CustGroup;
  }

  getServiceOrder() {
    return this.value.ServiceOrder;
  }

  getCostCenter() {
    return this.value.CostCenter;
  }

  public isValidForDate(momentDate: Moment): boolean {
    return momentDate.isBetween(
      convertSapDateToMoment(this.value.ValidFrom).startOf('day'),
      convertSapDateToMoment(this.value.ValidTo).endOf('day'),
    );
  }

  public doesContainSkf(querySkf: string): boolean {
    return (
      this.__equipmentSKF.some((eqp) => eqp.SKF === querySkf) ||
      this.__quantitySKF.some((qty) => qty.SKF === querySkf) ||
      this.__textSKF.some((txt) => txt.SKF === querySkf)
    );
  }

  private emptyStringIfNotOnContract(code: string) {
    if (!code) return '';
    if (this.conditions.materialList.find((x) => x.MaterialId === code)) return code;
    console.error(
      `Default mapping exists on ${this.value.CustGroup}, but billcode ${code} does not exists on ${this.value.ServiceOrder}. The app will use ${CatsNoBillCodeDescriptionEnum.Unassigned} instead of ${code}`,
    );
    return '';
  }

  getEmployeeDefault(activity: string, employeeNumber: string): string {
    function handleOpenAllCase(conditionsList: IConditionToDefault[]): string {
      //fourth case: ALL without specifying activity type
      const found = conditionsList.find((x) => x.Pernr === 'ALL');
      if (!found) return '';
      return found.BillCode;
    }

    function handleALLplus1DigitActtypeCase(actName: string, conditionsList: IConditionToDefault[]): string {
      //third case: ALL + 1 digit Activity type
      const workType = getFirstChar(actName);
      const found = conditionsList.find((x) => x.Pernr === 'ALL' && x.Activity === workType);
      if (!found) return handleOpenAllCase(conditionsList);
      return found.BillCode;
    }

    function handleActivityCase(actName: string, conditionsList: IConditionToDefault[]): string {
      //second case: 5 digit activity
      const found = conditionsList.find((x) => safeCompare(x.Activity, actName) && !trimLeadingZeros(x.Pernr));
      if (!found) return handleALLplus1DigitActtypeCase(actName, conditionsList);
      return found.BillCode;
    }

    function determineDefaultPriority(actName: string, empNum: string, conditionsList: IConditionToDefault[]): string {
      //first case: specific employee number + 1 digit Activity type
      const workType = getFirstChar(actName);
      const found = conditionsList.find((x) => x.Activity === workType && safeCompare(x.Pernr, empNum));
      if (!found) return handleActivityCase(actName, conditionsList);
      return found.BillCode;
    }

    const billcode = determineDefaultPriority(activity, employeeNumber, this.conditions.employeeBillingMap);
    return this.emptyStringIfNotOnContract(billcode);
  }

  getEquipmentDefault(equipment: Equipment) {
    function handleOpenAllCase(conditionsList: IConditionsForContract): string {
      //fourth case: ALL without specifying activity type
      const found = conditionsList.equipmentBillingMap.find((x) => x.EquipmentId === 'ALL');
      if (!found) return '';
      return found.BillCode;
    }

    function handleActivityCase(activity: string, conditionsList: IConditionsForContract): string {
      //second case: 2 digit activity
      const found = conditionsList.equipmentTypeBillingMap.find((x) => safeCompare(x.Activity, activity));
      if (!found) return handleOpenAllCase(conditionsList);
      return found.BillCode;
    }

    function determineDefaultPriority(activity: string, equipmentNumber: string, conditionsList: IConditionsForContract): string {
      //first case: specific equipment number
      const found = conditionsList.equipmentBillingMap.find((x) => safeCompare(x.EquipmentId, equipmentNumber));
      if (!found) return handleActivityCase(activity, conditionsList);
      return found.BillCode;
    }

    const billcode = determineDefaultPriority(equipment.Type, equipment.EquipmentId, this.conditions);
    return this.emptyStringIfNotOnContract(billcode);
  }

  getProductionDefault(skf: string) {
    const found = this.conditions.skfs.find((x) => x.SKF === skf);
    if (found) return this.emptyStringIfNotOnContract(found.BillCode);
    else return '';
  }

  getBillCodeDescription(code: string) {
    if (code === '') return CatsNoBillCodeDescriptionEnum.Unassigned;
    const found = this.conditions.materialList.find((x) => x.MaterialId === code);
    if (found) return found.Description;
    else return CatsNoBillCodeDescriptionEnum.NotOnContract;
  }

  private getBillCodes(): ISelectOptions[] {
    return this.conditions.materialList.map((x) => {
      return {
        value: x.MaterialId,
        description: x.Description,
      };
    });
  }

  public getEmployeeBillCodes(): ISelectOptions[] {
    return this.conditions.materialList
      .filter((x) => x.Group === materialCodes.MATERIAL_GROUP_LABOR_CODE)
      .map((x) => {
        return {
          value: x.MaterialId,
          description: x.Description,
        };
      });
  }

  public getEquipmentBillCodes(): ISelectOptions[] {
    return this.conditions.materialList
      .filter((x) => x.Group === materialCodes.MATERIAL_GROUP_EQUIPMENT_CODE)
      .map((x) => {
        return {
          value: x.MaterialId,
          description: x.Description,
        };
      });
  }

  public getSkfBillCodes(): ISelectOptions[] {
    return this.conditions.materialList
      .filter((x) => x.Group === materialCodes.MATERIAL_GROUP_PRODUCTION_CODE)
      .map((x) => {
        return {
          value: x.MaterialId,
          description: x.Description,
        };
      });
  }

  getBillCodesByType(type: BasicInterfaceTypeEnum): ISelectOptions[] {
    switch (type) {
      case BasicInterfaceTypeEnum.Employee:
        return this.getEmployeeBillCodes();
      case BasicInterfaceTypeEnum.Equipment:
        return this.getEquipmentBillCodes();
      case BasicInterfaceTypeEnum.ProductionEquipment:
        return this.getEquipmentBillCodes();
      case BasicInterfaceTypeEnum.ProductionQuantity:
        return this.getSkfBillCodes();
      case BasicInterfaceTypeEnum.ProductionText:
        return this.getSkfBillCodes();
      default:
        //unknown/missing type!
        return this.getBillCodes();
    }
  }

  getContractConditions(datahub: DatahubService) {
    if (this.__areConditionsFetched) return;
    const params = {
      ServiceOrder: this.value.ServiceOrder,
      KeyDate: `datetime'${getOdataString(datahub.weDate.getWeDate())}'`,
      CustGroup: this.value.CustGroup,
      CondGroup: this.value.CondGroup,
    };
    datahub.apiService.getContractConditions$(params).subscribe({
      next: (value) => {
        const found = value.find((x) => x.CondGroup === this.value.CondGroup && x.CustGroup === this.value.CustGroup);
        if (found) this.parseAndSetConditions(found);
        return !!found;
      },
      error: (err) => {
        console.error('Error fetching contract conditions:', err);
      },
    });
  }

  parseAndSetConditions(contractConditionEntity: ISapResponseContractCondition | ConditionHandler) {
    if (!this.__areConditionsFetched) {
      this.__areConditionsFetched = true; //only need to parse conditions once. It is possible in a race condition for this function to happen more than once.
      contractConditionEntity.CondToDefault.results?.forEach((defaultEntity: IConditionToDefault) => {
        if (defaultEntity.Activity?.length === 2) this.conditions.equipmentTypeBillingMap.push(defaultEntity);
        else if (defaultEntity.Activity?.length === 5) this.conditions.employeeBillingMap.push(defaultEntity);
        else if (defaultEntity.EquipmentId) this.conditions.equipmentBillingMap.push(defaultEntity);
        else if (defaultEntity.Pernr) this.conditions.employeeBillingMap.push(defaultEntity);
      });
      contractConditionEntity.CondToSkf.results?.forEach((x: ISkfConfigEntity) => {
        if (!this.supportedSKFTypes.find((supportedType) => x.Type === supportedType)) return;
        if (x.ProductionGroup) this.conditions.prdGroup.push(x);
        else this.conditions.skfs.push(x);
      });
      this.conditions.prdGroup.sort((a, b) => (a.SortOrder > b.SortOrder ? 1 : -1));
      this.conditions.materialList = contractConditionEntity.CondToMat.results ?? [];
      this.conditions.timesheetHeaders = contractConditionEntity.CondToTimesheetHdr.results ?? [];
      this.makeEmptyProduction();
    }
  }

  private makeEmptyProduction(): void {
    //this gets the configuration based on the contract for production.
    const skfQuantity: ISkfConfigEntity[] = [];
    const skfText: ISkfConfigEntity[] = [];
    const skfEquipment: ISkfConfigEntity[] = [];
    this.conditions.skfs.forEach((skf: ISkfConfigEntity) => {
      if (skf.Type === 'PRODUCTION') skfQuantity.push(skf);
      if (
        skf.Type === 'TEXT' ||
        skf.Type === 'SHORT_TEXT' ||
        skf.Type === 'SELECT' ||
        skf.Type === 'BOOLEAN' ||
        skf.Type === 'TIME'
      ) {
        skfText.push(skf);
      }
      if (skf.Type === 'EQUIPMENT') skfEquipment.push(skf);
    });
    this.__quantitySKF = skfQuantity;
    this.__textSKF = skfText;
    this.__equipmentSKF = skfEquipment;
    this.skfsReadySubject$.next(true);
  }

  getPrdGrpConfig(): ITableConfigEntity[] {
    const leftAlignedHeaderClasses = 'text-left pl-[5px]';
    const centerAlignedHeaderClasses = 'mr-2';
    //we don't want to statically call this, this is only used in one place, and many contracts are made on init.
    const stale: ITableConfigEntity[] = [
      makeComplexConfigItem<ProductionGroup>('Date', 'readableDate', 'text', 2, false, true, { class: 'h-full !text-left' }),
    ];
    const dynamics: ITableConfigEntity[] = this.conditions.prdGroup
      //eslint-disable-next-line complexity -- TODO: split up this logic to make this less of a complex method
      .map((skf, i) => {
        const nestedObjectName = ProductionGroup.magicGroupItemPropertyNamePrefix + i.toString(); //NOTE: This is a magical string property name that is dynamically created on these objects!
        if (skf.Type === 'TEXT') {
          return makeComplexConfigItem<ProductionGroupText>(skf.CustDesc, `LongTextstr`, 'text-input', 9, false, false, {
            headerClass: leftAlignedHeaderClasses,
            class: 'mr-2',
            nestedObjectName,
            selectOptions: skf.SkfToValue.results?.map((y) => makeSelectOption(y.Fldval, y.Fldval)),
            maxTextRows: 1,
          });
        } else if (skf.Type === 'SHORT_TEXT') {
          return makeComplexConfigItem<ProductionGroupText>(skf.CustDesc, `LongTextstr`, 'text-input', 3, false, false, {
            headerClass: leftAlignedHeaderClasses,
            class: 'mr-2',
            nestedObjectName,
            selectOptions: skf.SkfToValue.results?.map((y) => makeSelectOption(y.Fldval, y.Fldval)),
            maxTextLength: 15,
            maxTextRows: 1,
          });
        } else if (skf.Type === 'TIME') {
          return makeComplexConfigItem<ProductionGroupText>(skf.CustDesc, `LongTextstr`, 'time', 3, false, false, {
            headerClass: centerAlignedHeaderClasses,
            class: 'mr-2',
            nestedObjectName,
          });
        } else if (skf.Type === 'PRODUCTION') {
          return makeComplexConfigItem<ProductionGroupQuantity>(skf.CustDesc, `Quantity`, 'input', 4, false, false, {
            headerClass: centerAlignedHeaderClasses,
            class: 'pr-2',
            nestedObjectName,
            shouldTotal: true, //these are always number inputs, and we want to show totals for these only
          });
        } else if (skf.Type === 'SELECT') {
          const configSelectOptions = skf.SkfToValue.results?.map((y) => makeSelectOption(y.Fldval, y.Fldval)) ?? [];
          configSelectOptions.push({ value: '', description: '' }); //add an empty item at the end
          return makeComplexConfigItem<ProductionGroupText>(skf.CustDesc, `LongTextstr`, 'select', 4, false, false, {
            headerClass: leftAlignedHeaderClasses,
            nestedObjectName,
            selectOptions: configSelectOptions,
          });
        } else if (skf.Type === 'BOOLEAN') {
          return makeComplexConfigItem<ProductionGroupText>(skf.CustDesc, `LongTextstr`, 'checkbox', 3, false, false, {
            nestedObjectName,
          });
        }
        return undefined;
      })
      .filter((x) => x != null);
    dynamics.push(
      makeComplexConfigItem('', '', 'icon', 2, false, false, {
        svgIcon: 'move-time',
        shouldDetectClick: true,
        tooltip: 'Move to another timesheet',
        clickValue: 'moveTimeSheetClicked',
      }),
    );
    dynamics.push(
      makeComplexConfigItem('', '', 'icon', 1, false, false, {
        icon: 'delete',
        shouldDetectClick: true,
        tooltip: 'Delete',
        clickValue: 'deleteClicked',
      }),
    );
    return stale.concat(dynamics);
  }

  prdGroupExists(): boolean {
    return arrayIsTruthy(this.conditions.prdGroup);
  }

  getHeaderConfig(): ITableConfigEntity<keyof ExtDocument>[] {
    const headers = this.conditions.timesheetHeaders;

    return headers.map((element) => {
      const options = element.TimesheetConfigValuesSet.results?.map((x) => {
        return {
          value: x.Fldval,
          description: x.FldvaldescEnglish,
        };
      });
      //make this column width match the max column width defined in the <timesheet-headers> component
      return makeComplexConfigItem<ExtDocument>(
        element.Englishlabel,
        `Genericfld${element.TimeGenFld}` as keyof ExtDocument,
        element.Valtype,
        1,
        false,
        false,
        {
          selectOptions: options ?? [],
          hasInlineLabel: true,
          class: 'px-1',
          maxTextRows: 1,
        },
      );
    });
  }

  updateEmployeeDefault(defaultUpdateFields: IConditionToDefault): void {
    const found = this.conditions.employeeBillingMap.find(
      (c) => c.Pernr === defaultUpdateFields.Pernr && c.Activity === defaultUpdateFields.Activity,
    );
    if (found) found.BillCode = defaultUpdateFields.BillCode;
    else this.conditions.employeeBillingMap.push(defaultUpdateFields);
  }

  updateEquipmentDefault(defaultUpdateFields: IConditionToDefault): void {
    const found = this.conditions.equipmentBillingMap.find((c) => c.EquipmentId === defaultUpdateFields.EquipmentId);
    if (found) found.BillCode = defaultUpdateFields.BillCode;
    else this.conditions.equipmentBillingMap.push(defaultUpdateFields);
  }
}

interface IConditionsForContract {
  //values found on customer/condition group webservice
  // activityBillingMap: IConditionToDefault[]
  employeeBillingMap: IConditionToDefault[];
  equipmentBillingMap: IConditionToDefault[];
  equipmentTypeBillingMap: IConditionToDefault[];
  timesheetHeaders: ICondTimesheetHeaderResult[];
  prdGroup: ISkfConfigEntity[];
  skfs: ISkfConfigEntity[];
  materialList: IConditionToMaterialResult[];
}

export type ISupportedSkfTypes = 'PRODUCTION' | 'TEXT' | 'EQUIPMENT' | 'BOOLEAN' | 'SELECT' | 'SHORT_TEXT' | 'TIME';
